<article class="community-post" [class.community-post--nested]="depth > 0" *transloco="let t">
  <header class="community-post__header">
    @if (post.unread) {
      <span class="community-post__unread-dot" [title]="t('community.unread.post')"></span>
    }
    <div class="community-post__author">
      @if (authorLink; as link) {
        <a [routerLink]="link" class="community-post__avatar-link" [title]="t('community.member.view_profile')">
          <app-community-avatar [user]="post.user" [size]="32" />
        </a>
      } @else {
        <app-community-avatar [user]="post.user" [size]="32" />
      }
      <div>
        <div class="community-post__name">
          @if (authorLink; as link) {
            <a [routerLink]="link" class="community-post__author-link">{{ post.user?.display_name || '—' }}</a>
          } @else {
            {{ post.user?.display_name || '—' }}
          }
          @if (canModerate && post.user?.id) {
            <button type="button" class="btn btn-link btn-sm p-0 ms-1 community-post__mod-link"
                    [title]="t('community.moderation.summary.open')" (click)="onMemberSummary()">🛡</button>
          }
          <founding-member-badge class="ms-1"
                                 [foundingMember]="post.user?.founding_member"
                                 [memberNumber]="post.user?.founding_member_number" />
          <founding-member-badge class="ms-1" kind="practitioner"
                                 [foundingMember]="post.user?.founding_practitioner"
                                 [memberNumber]="post.user?.founding_practitioner_number" />
          <app-community-badges [badges]="post.user?.badges" />
          @if (post.user?.is_admin) {
            <span class="badge bg-danger ms-1">ADMIN</span>
          }
          @if (authorHidden) {
            <span class="badge bg-warning text-dark ms-1"
                  [title]="t('community.users.hidden_hint')">
              🚫 {{ t('community.users.hidden') }}
            </span>
          }
        </div>
        <div class="community-post__meta">
          {{ post.created_at | date:'medium' }}
          @if (post.edited_at) {
            <span class="community-post__edited ms-1" [title]="post.edited_at | date:'medium'">
              · {{ t('community.edited') }}
            </span>
          }
          @if (approvalBadge; as status) {
            <span class="badge ms-2 community-post__status"
                  [class.bg-warning]="status === 'pending'"
                  [class.text-dark]="status === 'pending'"
                  [class.bg-danger]="status === 'rejected'"
                  [title]="t('community.moderation.approval.' + status + '_hint')">
              {{ t('community.moderation.approval.' + status) }}
            </span>
          }
          @if (displayStatus !== 'visible') {
            <span class="badge ms-2 community-post__status"
                  [class.bg-danger]="displayStatus === 'deleted'"
                  [class.bg-warning]="displayStatus === 'hidden'"
                  [class.text-dark]="displayStatus === 'hidden'">
              @if (displayStatus === 'deleted') { {{ t('community.threads.deleted') }} }
              @if (displayStatus === 'hidden')  { {{ t('community.threads.hidden') }} }
            </span>
          }
          @if (isAcceptedAnswer) {
            <span class="badge bg-success ms-2">✓ {{ t('community.qa.accepted_answer') }}</span>
          }
        </div>
      </div>
    </div>
  </header>

  <div class="community-post__body">
    @if (post.masked) {
      <em class="text-muted">{{ t('community.threads.deleted') }}</em>
    } @else if (editOpen()) {
      <app-community-editor [value]="editDraft" (valueChange)="editDraft = $event"
                                 [channelId]="channelId" [canMention]="canMention" [canUploadImage]="canUploadImage"
                                 [canUploadFile]="canUploadFile" [canEmbedVideo]="canEmbedVideo"
                                 (uploadError)="uploadError.emit($event)"></app-community-editor>
      <div class="mt-2">
        <button type="button" class="btn btn-sm btn-primary me-2" [disabled]="bodyBlank(editDraft)" (click)="submitEdit()">
          {{ t('community.thread.actions.edit') }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelEdit()">
          {{ t('community.threads.cancel') }}
        </button>
      </div>
    } @else {
      <app-community-translate class="community-post__html" kind="post" [targetId]="post.id"
                               [originalBody]="post.body || ''" [sourceLocale]="post.locale"
                               [available]="translationAvailable" />
    }
  </div>

  @if (!post.masked && !editOpen()) {
    <footer class="community-post__actions">
      @if (!post.deleted_at) {
        <app-community-reactions class="me-2"
                                 target="post"
                                 [targetId]="post.id"
                                 [reactionSet]="reactionSet"
                                 [reactions]="post.reactions"
                                 [myReactions]="post.my_reactions"
                                 [canReact]="post.can_react === true"
                                 (failed)="actionFailed.emit($event)" />
      }
      @if (canReply && !threadLocked && !post.deleted_at && !post.hidden_at) {
        <button type="button" class="btn btn-sm btn-link" (click)="openReply()">
          {{ t('community.thread.reply') }}
        </button>
      }
      @if (post.can_modify && !post.deleted_at) {
        <button type="button" class="btn btn-sm btn-link" (click)="openEdit()">
          {{ t('community.thread.actions.edit') }}
        </button>
      }
      @if (canDelete && !post.deleted_at) {
        <button type="button" class="btn btn-sm btn-link text-danger" (click)="onDelete()">
          {{ t('community.thread.actions.delete') }}
        </button>
      }
      @if (canReport) {
        <button type="button" class="btn btn-sm btn-link text-muted" (click)="onReport()">
          🚩 {{ t('community.report.action') }}
        </button>
      }
      @if (canSolve && !post.deleted_at && !post.hidden_at) {
        @if (!isAcceptedAnswer) {
          <button type="button" class="btn btn-sm btn-link text-success" (click)="onMarkSolved()">
            ✓ {{ t('community.qa.mark_solved') }}
          </button>
        } @else {
          <button type="button" class="btn btn-sm btn-link text-muted" (click)="onUnmarkSolved()">
            {{ t('community.qa.unmark') }}
          </button>
        }
      }
      @if (canModerate) {
        @if (!post.hidden_at) {
          <button type="button" class="btn btn-sm btn-link text-warning" (click)="onHide(true)">
            {{ t('community.thread.actions.hide') }}
          </button>
        } @else {
          <button type="button" class="btn btn-sm btn-link text-warning" (click)="onHide(false)">
            {{ t('community.thread.actions.unhide') }}
          </button>
        }
        <button type="button" class="btn btn-sm btn-link text-info" (click)="toggleHistory()">
          @if (historyOpen()) { {{ t('community.history.hide') }} }
          @else { {{ t('community.history.show') }} }
        </button>
        @if (canHideAuthor) {
          @if (!authorHidden) {
            <button type="button" class="btn btn-sm btn-link text-danger" (click)="onHideUser(true)">
              🚫 {{ t('community.users.hide') }}
            </button>
          } @else {
            <button type="button" class="btn btn-sm btn-link text-success" (click)="onHideUser(false)">
              {{ t('community.users.unhide') }}
            </button>
          }
        }
      }
    </footer>

    @if (historyOpen() && canModerate) {
      <app-community-history target="post" [targetId]="post.id"></app-community-history>
    }
  }

  @if (replyOpen()) {
    <div class="community-post__reply-form">
      <app-community-editor [value]="replyDraft" (valueChange)="replyDraft = $event"
                                 [channelId]="channelId" [canMention]="canMention" [canUploadImage]="canUploadImage"
                                 [canUploadFile]="canUploadFile" [canEmbedVideo]="canEmbedVideo"
                                 (uploadError)="uploadError.emit($event)"></app-community-editor>
      <div class="mt-2">
        <button type="button" class="btn btn-sm btn-primary me-2" [disabled]="bodyBlank(replyDraft)" (click)="submitReply()">
          {{ t('community.thread.reply') }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelReply()">
          {{ t('community.threads.cancel') }}
        </button>
      </div>
    </div>
  }

  @if (post.replies.length) {
    <div class="community-post__replies">
      @for (child of post.replies; track child.id) {
        <app-community-post
          [post]="child"
          [threadLocked]="threadLocked"
          [canReply]="canReply"
          [reactionSet]="reactionSet"
          [depth]="depth + 1"
          [canSolve]="canSolve"
          [solvedPostId]="solvedPostId"
          [translationAvailable]="translationAvailable"
          [channelId]="channelId"
          [canMention]="canMention"
          [canUploadImage]="canUploadImage"
          [canUploadFile]="canUploadFile"
          [canEmbedVideo]="canEmbedVideo"
          (uploadError)="onChildUploadError($event)"
          (reply)="onChildReply($event)"
          (actionFailed)="onChildActionFailed($event)"
          (edit)="onChildEdit($event)"
          (delete)="onChildDelete($event)"
          (toggleHide)="onChildToggleHide($event)"
          (toggleHideUser)="onChildToggleHideUser($event)"
          (report)="onChildReport($event)"
          (memberSummary)="onChildMemberSummary($event)"
          (markSolved)="onChildMarkSolved($event)"
          (unmarkSolved)="onChildUnmarkSolved()">
        </app-community-post>
      }
    </div>
  }
</article>
